import React from 'react';
import { TrendingUp, Shield, Users, Rocket, ChevronRight, BarChart2, Globe, Zap } from 'lucide-react';
import { SEO } from "../components/SEO.tsx";

export function AboutUs() {
  return (
    <div className="bg-gray-50">
      <SEO
        title="About Us"
        description="Learn about Wayja, the prediction markets platform. Discover how we empower users to trade on real-world events and harness collective intelligence."
        url="/about"
      />
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-wayja-teal/90 to-wayja-teal/60 z-0"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-wayja-teal/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-24 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
              Predict. Trade. <span className="text-wayja-teal">Win.</span>
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Wayja is revolutionizing prediction markets by creating an intuitive platform where 
              your insights turn into opportunities.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a 
                href="/auth/register" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-white text-wayja-teal font-semibold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                Join Wayja Today
                <ChevronRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="/markets" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-transparent border-2 border-white text-white font-semibold hover:bg-white/10 transition-all"
              >
                Explore Markets
              </a>
            </div>
          </div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#f9fafb" fillOpacity="1" d="M0,96L80,80C160,64,320,32,480,32C640,32,800,64,960,69.3C1120,75,1280,53,1360,42.7L1440,32L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </div>
      
      {/* Mission Statement in a card */}
      <div className="max-w-5xl mx-auto px-4 -mt-6 relative z-20">
        <div className="bg-white rounded-xl shadow-xl p-8 flex flex-col md:flex-row items-center gap-6">
          <div className="bg-wayja-teal/10 p-4 rounded-full">
            <TrendingUp className="w-12 h-12 text-wayja-teal" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              Wayja empowers individuals to express their opinions and insights through prediction markets. 
              We believe in the wisdom of crowds and that markets are the most effective mechanism for 
              aggregating information and forecasting outcomes.
            </p>
          </div>
        </div>
      </div>
      
      {/* How Wayja Works - Feature Cards */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How Wayja Works</h2>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 border-t-4 border-wayja-teal">
            <div className="mb-4">
              <div className="bg-wayja-teal/10 w-14 h-14 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-8 h-8 text-wayja-teal" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">Real-World Events</h3>
            <p className="text-gray-600">
              Place trades on outcomes across sports, politics, technology, and more—expanding your prediction portfolio.
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 border-t-4 border-wayja-teal">
            <div className="mb-4">
              <div className="bg-wayja-teal/10 w-14 h-14 rounded-lg flex items-center justify-center">
                <Globe className="w-8 h-8 text-wayja-teal" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">Collective Intelligence</h3>
            <p className="text-gray-600">
              Join a community of forecasters whose combined insights create powerful predictive signals.
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 border-t-4 border-wayja-teal">
            <div className="mb-4">
              <div className="bg-wayja-teal/10 w-14 h-14 rounded-lg flex items-center justify-center">
                <Zap className="w-8 h-8 text-wayja-teal" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">Intuitive Interface</h3>
            <p className="text-gray-600">
              Whether you're a seasoned forecaster or newcomer, our platform makes navigation and trading simple.
            </p>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-gray-600 italic">
            "By participating in these markets, you're not just placing trades—you're contributing to a collective intelligence that often outperforms traditional forecasting methods."
          </p>
        </div>
      </div>
      
      {/* Trust and Transparency with Stats */}
      <div className="bg-gray-900 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-white mb-6">Trust and Transparency</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                We've built Wayja on principles of trust and transparency. Our platform features real-time 
                odds that reflect the collective wisdom of all participants. Your funds are secured through 
                our robust wallet system, and our Supabase-powered backend ensures data integrity and security.
              </p>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-wayja-teal" />
                <span className="text-white">Secure wallet management</span>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <Shield className="w-6 h-6 text-wayja-teal" />
                <span className="text-white">Transparent market resolution</span>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <Shield className="w-6 h-6 text-wayja-teal" />
                <span className="text-white">Real-time odds updates</span>
              </div>
            </div>
            
            <div className="md:w-1/2 grid grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-wayja-teal mb-2">100%</div>
                <div className="text-gray-300">Transparent Resolution</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-wayja-teal mb-2">24/7</div>
                <div className="text-gray-300">Market Availability</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-wayja-teal mb-2">8%</div>
                <div className="text-gray-300">Service Fee</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-wayja-teal mb-2">0</div>
                <div className="text-gray-300">Hidden Charges</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Our Team and Vision */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Team and Vision</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              The Wayja team consists of prediction market enthusiasts, experienced developers, and 
              design-focused professionals dedicated to creating the best user experience possible.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our focus on user experience, innovative features, and constant improvement drives 
              everything we do. We're building Wayja not just as a prediction platform, but as a community 
              of forward-thinking individuals who enjoy testing their knowledge and foresight.
            </p>
          </div>
          
          <div className="md:w-1/2">
            <div className="bg-gradient-to-br from-gray-100 to-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Rocket className="w-6 h-6 text-wayja-teal mr-2" />
                Our Roadmap
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-wayja-teal/20 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-wayja-teal"></div>
                  </div>
                  <span className="text-gray-700">Mobile app for on-the-go predictions</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-wayja-teal/20 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-wayja-teal"></div>
                  </div>
                  <span className="text-gray-700">Influencer-created markets and communities</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-wayja-teal/20 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-wayja-teal"></div>
                  </div>
                  <span className="text-gray-700">Blockchain-based ledger for enhanced transparency</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-wayja-teal/20 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-wayja-teal"></div>
                  </div>
                  <span className="text-gray-700">Smart contract resolution systems</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Testimonials or Social Proof */}
      <div className="bg-wayja-teal/5 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">What Our Users Say</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-md p-6 relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-wayja-teal rounded-full flex items-center justify-center shadow-lg">
                <span className="text-2xl text-white font-bold">"</span>
              </div>
              <p className="text-gray-600 mt-4 mb-6 italic">
                "Wayja has completely changed how I think about predictions. The platform is intuitive, the community is engaging, and I've been able to profit from my insights."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-500" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Alex K.</div>
                  <div className="text-sm text-gray-500">Forecaster since 2022</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6 relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-wayja-teal rounded-full flex items-center justify-center shadow-lg">
                <span className="text-2xl text-white font-bold">"</span>
              </div>
              <p className="text-gray-600 mt-4 mb-6 italic">
                "The transparency of Wayja's markets gives me confidence. I love tracking political outcomes and have found the real-time updates incredibly accurate."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-500" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Sarah M.</div>
                  <div className="text-sm text-gray-500">Political analyst</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Join CTA section */}
      <div className="relative py-16 bg-gray-900 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-wayja-teal/10 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-wayja-teal/10 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Join Our Community</h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-8">
              We invite you to join Wayja and become part of the future of forecasting. Test your predictive abilities, 
              learn from others, and help shape the future of prediction markets.
            </p>
            
            <div className="inline-block bg-white p-1 rounded-lg shadow-lg">
              <a 
                href="/auth/register" 
                className="inline-flex items-center px-8 py-4 rounded-md bg-gradient-to-r from-wayja-teal to-wayja-teal/70 text-white font-bold text-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                Start Predicting Today
                <ChevronRight className="ml-2 w-6 h-6" />
              </a>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-wayja-teal" />
                <span className="text-white">Secure Wallet System</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-wayja-teal" />
                <span className="text-white">Growing Community</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-wayja-teal" />
                <span className="text-white">Real-time Market Updates</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AboutUs; 